import { use, useEffect, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import { DeviceType } from 'shared/models/device-type';
import DevicesProviderContext from 'shared/providers/devices/contexts/devices-provider-context';

import { useDevices } from 'features/device/devices/hooks/use-devices';
import Device from 'features/device/domain/models/device';
import { useDeleteDevice } from 'features/device/domain/queries/device-queries';

export interface IDeviceDeleteHook {
  deleteDevice: () => void;
  deleteDeviceIsLoading: boolean;
  closeDeleteConfirmationPopup: () => void;
  closeDeleteErrorPopup: () => void;
  isDeleteConfirmationPopupOpen: boolean;
  isDeleteErrorPopupOpen: boolean;
  deleteErrorMessageId?: string;
}

export const useDeviceDeleteHook = (device: Device | undefined, deleteCallbackAction: () => void): IDeviceDeleteHook => {
  const { setErrorMessage, setCustomErrorMessage } = use(ErrorMessageProviderContext);
  const { triggerReadDevicesQuery, readDevicesQuery } = use(DevicesProviderContext);

  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState<boolean>(false);
  const [isDeleteErrorPopupOpen, setIsDeleteErrorPopupOpen] = useState<boolean>(false);
  const [deleteErrorMessageId, setDeleteErrorMessageId] = useState<string>('');

  const { closeDeviceDetailsPopup } = useDevices();

  const [currentDevice, setCurrentDevice] = useState<Device>();

  const ipUnitWirelessDeviceTypes = [
    DeviceType.G2Contact,
    DeviceType.G2DoorContact,
    DeviceType.G2MotionDetector,
    DeviceType.G2SmokeAlarm,
    DeviceType.G2TableAlarm,
    DeviceType.G2ToiletAlarm,
    DeviceType.G2Transmitter,
  ];

  const {
    mutate: deleteDeviceMutation,
    isSuccess: deleteDeviceIsSuccess,
    isPending: deleteDeviceIsLoading,
    isError: deleteDeviceIsError,
    error: deleteDeviceError,
  } = useDeleteDevice();

  useEffect(() => {
    if (deleteDeviceIsSuccess) {
      setIsDeleteConfirmationPopupOpen(false);
      closeDeviceDetailsPopup();
      deleteCallbackAction();
      triggerReadDevicesQuery(readDevicesQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteDeviceIsSuccess]);

  useEffect(() => {
    if (deleteDeviceIsError && deleteDeviceError) {
      if (currentDevice?.type && ipUnitWirelessDeviceTypes.includes(currentDevice.type)) {
        setCustomErrorMessage('ipUnitSystemUnavailable');
      } else {
        setErrorMessage(deleteDeviceError);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteDeviceIsError]);

  useEffect(() => {
    if (device?.type === DeviceType.DesoDetectionLoop && device.hasWanderingBoundaryPassages) {
      setDeleteErrorMessageId('wanderingBoundaryPassagesBodyText');
      setIsDeleteErrorPopupOpen(true);
    } else if (device?.organisationUnitId) {
      setDeleteErrorMessageId('organisationUnitBodyText');
      setIsDeleteErrorPopupOpen(true);
    } else {
      setIsDeleteConfirmationPopupOpen(true);
      setCurrentDevice(device);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeDeleteConfirmationPopup = (): void => {
    setIsDeleteConfirmationPopupOpen(false);
  };

  const closeDeleteErrorPopup = (): void => {
    setIsDeleteErrorPopupOpen(false);
  };

  const deleteDevice = (): void => {
    deleteDeviceMutation(currentDevice?.id ?? '');
  };

  return {
    closeDeleteConfirmationPopup,
    closeDeleteErrorPopup,
    deleteDevice,
    deleteDeviceIsLoading,
    isDeleteConfirmationPopupOpen,
    isDeleteErrorPopupOpen,
    deleteErrorMessageId,
  };
};
